import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import type { SxProps, Theme } from "@mui/material/styles";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import { Chip } from "ui/chip";
import { Typography } from "ui/typography";
import { IconButton } from "ui/icon-button";
import TouchpointVersionsTableActionsMenu from "containers/admin/clients/touchpoint/tab-components/version-history-tab/components/touchpoint-versions-table-actions-menu/touchpoint-versions-table-actions-menu";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

import { getDisplayTouchpointType, formatFiscalYear } from "utilities";
import { getBatchPath } from "utilities/app-routes";
import { getPermittedActions } from "containers/admin/clients/touchpoint/tab-components/version-history-tab/helpers/versions-table";
import { formatDateTime } from "utilities/date";

import { TouchpointStatuses } from "types";
import { TouchpointType } from "types/touchpoint";
import { TableActionsProps } from "containers/admin/clients/touchpoint/tab-components/version-history-tab/components/touchpoint-version-table/touchpoint-version-table.types";

import Icons from "assets";

const headerStyles = "text-gray-600 font-bold leading-5";

const CELL_TEXT_SX: SxProps<Theme> = (theme) => ({
  color: theme.palette.grey[600],
  fontWeight: 500,
});

export const createTouchpointVersionTableColumns = ({
  touchpointVersionStatuses,
  openTouchpointEdit,
  openTouchpointPreview,
  touchpointType,
  isStudentPopulation,
  permissionToPublish,
  isTemplate,
  batches = [],
  clientId,
  isClientUser = false,
  isBatchManagementEnabled = false,
  // Modal controls
  onSetCurrentActionItem,
  onSetSelectedVersion,
  onOpenConfirmArchiveModal,
  onOpenCloneTouchpointModal,
  onOpenCloneTemplateModal,
  onOpenConfirmPublishModal,
  onOpenEditVersionModal,
  onOpenNewDraftModal,
  onOpenCreateParentTouchpointModal,
  onOpenUpdateClientThemeModal,
}: TableActionsProps): ColumnDef<TouchpointVersionAttributes>[] => [
  {
    id: "version",
    header: "Version",
    accessorKey: "version",
    enableSorting: true,
    cell: ({ row }) => {
      return (
        <div className="flex flex-col pr-3">
          <div className="text-navy-800 font-semibold" aria-disabled={false} tabIndex={undefined}>
            <div>v{row.original.version}</div>
          </div>
          <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
        </div>
      );
    },
    size: 210,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  ...(isTemplate
    ? []
    : [
        {
          id: "fiscalYear",
          header: "Fiscal year",
          accessorKey: "fiscalYear",
          cell: ({ row }) => {
            return (
              <span className="text-gray-600 font-medium">
                {formatFiscalYear(row.original.fiscalYear)}
              </span>
            );
          },
          size: 100,
        } satisfies ColumnDef<TouchpointVersionAttributes>,
      ]),
  {
    id: "description",
    header: "Description",
    accessorKey: "description",
    enableSorting: true,
    cell: ({ row }) => {
      return (
        <Typography component="span" variant="body2" sx={CELL_TEXT_SX}>
          {row.original.description}
        </Typography>
      );
    },
    size: 300,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "status",
    header: "Client review status",
    accessorKey: "status",
    enableSorting: true,
    cell: ({ row }) => {
      const statuses = touchpointVersionStatuses.find(
        (status) => status.status === row.original.status,
      )!;

      return <StatusIndicator versionStatuses={statuses} />;
    },
    size: 300,
    meta: {
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  ...(isBatchManagementEnabled && clientId
    ? [
        {
          id: "batch",
          header: "Batch",
          enableSorting: false,
          cell: ({ row }) => {
            const batchId = row.original.touchpointBatchId;

            if (!batchId) {
              return null;
            }

            const batchName = batches.find((batch) => batch.id === batchId)?.name;
            const batchPath = getBatchPath({ clientId, batchId, isClientUser });

            return (
              <Link
                to={batchPath}
                className="flex min-w-0 items-center gap-1 text-aqua-600 hover:text-aqua-700 font-semibold"
                aria-disabled={false}
                tabIndex={undefined}>
                <Icons.Layers className="size-4 shrink-0" aria-hidden />
                <span className="line-clamp-1">{batchName ?? "View batch"}</span>
              </Link>
            );
          },
          size: 200,
          meta: {
            headerClassName: `${headerStyles} pl-4`,
          },
        } satisfies ColumnDef<TouchpointVersionAttributes>,
      ]
    : []),
  ...(touchpointType === TouchpointType.EMAIL
    ? [
        {
          id: "themeStatus",
          header: "Theme status",
          enableSorting: false,
          cell: ({ row }) => {
            const isThemed = !!row?.original?.applyClientTheme === true;

            return (
              <Chip
                sx={{ textTransform: "none" }}
                size="small"
                variant="outlined"
                color={isThemed ? "success" : "default"}
                label={isThemed ? "Applied" : "Not applied"}
              />
            );
          },
          size: 140,
          meta: {
            headerClassName: `${headerStyles} pl-4`,
          },
        } satisfies ColumnDef<TouchpointVersionAttributes>,
      ]
    : []),
  {
    id: "actions",
    header: "Actions",
    enableSorting: false,
    cell: ({ row }) => {
      const currentTouchpointVersionType = getDisplayTouchpointType(touchpointType);

      const viewBtnText = `Preview ${currentTouchpointVersionType} Version`;
      const editBtnText = `Edit ${currentTouchpointVersionType} Version`;
      const { isEditable, versionIsPublished } = getPermittedActions(
        row.original.status as TouchpointStatuses,
      );

      return (
        <div className="grid auto-cols-[36px] grid-flow-col grid-rows-1 items-center justify-items-center gap-3">
          {isEditable ? (
            <IconButton
              variant="text"
              aria-label={editBtnText}
              size="medium"
              onClick={() => openTouchpointEdit(row.original)}>
              <Icons.Edit />
            </IconButton>
          ) : (
            <span aria-hidden="true" />
          )}
          <IconButton
            variant="text"
            aria-label={viewBtnText}
            size="medium"
            onClick={() => openTouchpointPreview(row.original.id)}>
            <Icons.EyeVisible />
          </IconButton>
          <TouchpointVersionsTableActionsMenu
            item={{
              itemName: "Version history",
              itemId: row.original.id,
            }}
            {...{
              version: row.original,
              versionIsPublished,
              isEditable,
              currentTouchpointVersionType,
              isStudentPopulation,
              permissionToPublish,
              // Modal controls
              onSetCurrentActionItem,
              onSetSelectedVersion,
              onOpenConfirmArchiveModal,
              onOpenCloneTouchpointModal,
              onOpenCloneTemplateModal,
              onOpenConfirmPublishModal,
              onOpenEditVersionModal,
              onOpenNewDraftModal,
              onOpenCreateParentTouchpointModal,
              onOpenUpdateClientThemeModal,
            }}
          />
        </div>
      );
    },
    size: 156,
    meta: {
      className: "pr-8",
      headerClassName: `${headerStyles} pl-4`,
      isPinnedRight: true,
    },
  },
];
